<script setup>
/**
 * A row of summary numbers, most of which open the list they count.
 *
 * Generic in the cards rather than in named props: this started as four
 * hardcoded numbers for the Policyholders page and could not take the
 * Overview's five, which is the usual end of a component that knows its
 * callers by name.
 *
 * A card with `clickable: false` renders as plain text with nothing to tab to
 * — XlKpi swaps the element rather than styling a dead button. That is how the
 * Overview's total is shown: it is the only number in the set with no list
 * behind it, so it is context rather than a doorway.
 *
 * A clickable card whose value is nought stays clickable. An empty list that
 * proves the number is a better answer than a control that refuses to explain
 * itself.
 */
import XlKpi from './XlKpi.vue';

const props = defineProps({
  /**
   * @type {{ key: string, label: string, value: number|string, hint?: string,
   *          tone?: string, clickable?: boolean, tooltip?: string, testid?: string,
   *          loading?: boolean }[]}
   */
  cards: { type: Array, default: () => [] },
});

const emit = defineEmits(['select']);

// Falls back to the label so callers that do not care about test ids still get
// stable ones, but an explicit `testid` wins — a label is copy and will change.
const testId = (card) => card.testid || `kpi-${card.label.toLowerCase().replace(/\s+/g, '-')}`;

const isClickable = (card) => card.clickable !== false;

const hintFor = (card) => {
  return card.hint ?? (isClickable(card) ? 'open the list' : '');
};
</script>

<template>
  <div class="xl-kpi-row" :style="{ '--xl-kpi-count': props.cards.length }">
    <XlKpi
      v-for="card in props.cards"
      :key="card.key"
      :clickable="isClickable(card)"
      :label="card.label"
      :value="card.value"
      :tone="card.tone || ''"
      :hint="hintFor(card)"
      :tooltip="card.tooltip || ''"
      :loading="Boolean(card.loading)"
      :data-testid="testId(card)"
      @select="emit('select', card.key)"
    />
  </div>
</template>

<style scoped>
.xl-kpi-row {
  display: grid;
  grid-template-columns: repeat(var(--xl-kpi-count, 4), 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

/* Five across is too many below a laptop, and two rows of uneven length read
   worse than three-then-two. */
@media (max-width: 1100px) {
  .xl-kpi-row { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 820px) {
  .xl-kpi-row { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 620px) {
  .xl-kpi-row { grid-template-columns: 1fr; }
}
</style>
